import React, { useState } from 'react'
import { useUser } from '../contexts/UserContext'
import { X, Mail, Lock, Eye, EyeOff, User } from 'lucide-react'
import BrandLogo from './BrandLogo'

const AuthModal = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const [mode, setMode] = useState(initialMode)

  React.useEffect(() => {
    if (isOpen) setMode(initialMode)
  }, [initialMode, isOpen])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [resetSentTo, setResetSentTo] = useState('')
  const [resetError, setResetError] = useState('')

  const { signIn, signUp, signInWithGoogleAuth, sendPasswordReset } = useUser()

  const openReset = () => {
    setMode('reset')
    setPassword('')
    setResetSentTo('')
    setResetError('')
  }

  const backToSignIn = () => {
    setMode('signin')
    setResetSentTo('')
    setResetError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    if (mode === 'reset') {
      setResetError('')
      try {
        setResetSentTo(await sendPasswordReset(email))
      } catch (error) {
        setResetError(error.message)
      } finally {
        setIsLoading(false)
      }
      return
    }

    try {
      if (mode === 'signup') {
        await signUp(email, password, name)
      } else {
        await signIn(email, password)
      }
      onClose()
    } catch (error) {
      console.error('Auth error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin')
    setEmail('')
    setPassword('')
    setName('')
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      await signInWithGoogleAuth()
      onClose()
    } catch (error) {
      console.error('Google sign-in error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  const inputClass =
    'w-full pl-10 pr-3 py-2.5 bg-ink-soft border border-ivory/15 rounded-lg text-ivory placeholder:text-ivory-faint focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors'
  const ssoBtnClass =
    'w-full flex items-center justify-center px-4 py-3 border border-ivory/15 rounded-lg bg-ink-soft text-ivory hover:border-ivory/30 hover:bg-ink-raised disabled:opacity-50 disabled:cursor-not-allowed transition-colors'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div className="relative w-full max-w-md rounded-xl border border-ivory/10 bg-ink-soft p-8 shadow-2xl shadow-black/50">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-ivory-faint hover:text-ivory transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="flex justify-center mb-6">
          <BrandLogo size="auth" />
        </div>
        <h2 id="auth-modal-title" className="text-2xl font-bold text-ivory mb-6">
          {mode === 'reset' ? 'Reset your password' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </h2>

        {mode === 'reset' ? (
          <div>
            {resetSentTo ? (
              <p className="text-sm text-ivory-muted mb-6" role="status">
                If an account uses {resetSentTo}, we sent it a link to reset your password.
                Check your inbox and spam folder.
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-ivory-muted">
                  Enter the email you sign in with and we&apos;ll send you a link to choose a new password.
                </p>
                <div>
                  <label htmlFor="auth-reset-email" className="block text-sm font-medium text-ivory-muted mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-faint w-5 h-5" />
                    <input
                      id="auth-reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder="Enter your email"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                {resetError && (
                  <p className="text-sm text-red-300" role="alert">{resetError}</p>
                )}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gold text-ink py-3 px-4 rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>
            )}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={backToSignIn}
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : (
        <>
        <div className="space-y-3 mb-6">
          {mode === 'signup' && (
            <p className="text-sm text-ivory-muted text-center">
              New account? Use Google below. No password needed. We&apos;ll create your profile when you continue.
            </p>
          )}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className={ssoBtnClass}
          >
            <svg className="w-5 h-5 mr-3 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {mode === 'signup' ? 'Sign up with Google' : 'Continue with Google'}
          </button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-ivory/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-ink-soft text-ivory-faint">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-ivory-muted mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-faint w-5 h-5" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  placeholder="Enter your full name"
                  required={mode === 'signup'}
                  autoComplete="name"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ivory-muted mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-faint w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ivory-muted mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory-faint w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} pr-10`}
                placeholder="Enter your password"
                required
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory-faint hover:text-ivory transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {mode === 'signin' && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={openReset}
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gold text-ink py-3 px-4 rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-ink border-t-transparent rounded-full animate-spin mr-2" />
                {mode === 'signin' ? 'Signing In...' : 'Creating Account...'}
              </div>
            ) : (
              mode === 'signin' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-ivory-muted">
            {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}
            <button
              type="button"
              onClick={toggleMode}
              className="ml-1 text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              {mode === 'signin' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
        </>
        )}

        <div className="mt-6 pt-6 border-t border-ivory/10">
          <p className="text-xs text-ivory-faint text-center">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  )
}

export default AuthModal
